import { CommonModule } from "@angular/common";
import { Component, computed, effect, input, model, Pipe, PipeTransform, signal, untracked } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { NzButtonModule } from "ng-zorro-antd/button";
import { NzCardComponent } from "ng-zorro-antd/card";
import { NzGridModule } from "ng-zorro-antd/grid";
import { NzIconModule } from "ng-zorro-antd/icon";
import { NzInputModule } from "ng-zorro-antd/input";
import { NzSelectModule } from "ng-zorro-antd/select";
import { NzTooltipDirective } from "ng-zorro-antd/tooltip";
import {
  DEFAULT_FILE_COLOR,
  DEFAULT_FILE_ICON,
  FileMeta,
  FileSpec,
  toFileMeta,
} from "../job-type-config";
import { Job } from "../../../../models/job";
import { JobVolumePickerComponent } from "./job-volume-picker/job-volume-picker.component";

@Pipe({ name: 'filesFor', standalone: true, pure: true })
export class FilesForPipe implements PipeTransform {
  transform(spec: FileSpec, allFiles: FileMeta[], runfiles: FileMeta[]): FileMeta[] {
    const source = spec.source === 'runfiles' ? runfiles : allFiles;
    if (!spec.regex) return source;
    const re = new RegExp(spec.regex, 'i');
    return source.filter((f) => re.test(f.path));
  }
}

@Component({
  selector: 'app-file-assignment',
  templateUrl: './file-assignment.component.html',
  styleUrl: './file-assignment.component.less',
  imports: [
    CommonModule,
    FormsModule,
    NzButtonModule,
    NzCardComponent,
    NzGridModule,
    NzIconModule,
    NzInputModule,
    NzSelectModule,
    NzTooltipDirective,
    FilesForPipe,
    JobVolumePickerComponent,
  ],
})
export class FileAssignmentComponent {
  readonly fileSpecs = input<FileSpec[]>([]);
  readonly allFiles = input<FileMeta[]>([]);
  readonly runfiles = input<FileMeta[]>([]);
  readonly loading = input<boolean>(false);
  readonly defaultBasePath = input<string>('');
  readonly xwiPrefix = input<string>('');
  /** Jobs available to source a volume from (for sourceFromJob specs). */
  readonly jobs = input<Job[]>([]);
  /** Template job — used as the default source for sourceFromJob specs. */
  readonly templateJob = input<Job | null>(null);

  /** Resolved map of key → FileMeta (file path data only, no names). */
  readonly resolvedSelections = model<Record<string, FileMeta>>({});
  /** Resolved map of key → output name, updated independently of file selections. */
  readonly resolvedRenames = model<Record<string, string>>({});

  readonly defaultIcon = DEFAULT_FILE_ICON;
  readonly defaultColor = DEFAULT_FILE_COLOR;

  protected fileSelections = signal<Record<string, FileMeta>>({});
  protected customPaths = signal<Record<string, string>>({});
  protected fileRenames = signal<Record<string, string>>({});
  protected extraKeys = signal<string[]>([]);
  private _extraKeyCounter = 0;

  protected extraSpecs = computed<FileSpec[]>(() =>
    this.extraKeys().map((key, i) => ({
      key,
      label: `Extra File ${i + 1}`,
      source: 'all_files' as const,
      icon: DEFAULT_FILE_ICON,
      color: DEFAULT_FILE_COLOR,
    }))
  );

  protected allRenderSpecs = computed<FileSpec[]>(() => [
    ...this.fileSpecs(),
    ...this.extraSpecs(),
  ]);

  private _resolved = computed<Record<string, FileMeta>>(() => {
    const selections = this.fileSelections();
    const customs = this.customPaths();
    const result: Record<string, FileMeta> = {};
    for (const s of this.allRenderSpecs()) {
      const custom = customs[s.key]?.trim();
      const value = custom ? toFileMeta(custom) : selections[s.key];
      if (value) result[s.key] = value;
    }
    return result;
  });

  constructor() {
    // Reset all state when the set of file specs changes (new job type).
    // This is keyed ONLY on fileSpecs — NOT on allFiles/runfiles. Those load
    // asynchronously and, for a cached (re-selected) template, arrive AFTER the
    // JobVolumePicker has already seeded its velocity. Tracking them here would
    // re-run this wipe and clobber that selection — the picker, seeing no change
    // to its own signal, would then never re-emit. (See default-seeding below.)
    effect(() => {
      const specs = this.fileSpecs();
      this.fileSelections.set({});
      this.customPaths.set({});

      untracked(() => {
        const prefix = this.xwiPrefix();

        // Apply default renames (prefixed with xwi_prefix)
        const renames: Record<string, string> = {};
        for (const spec of specs) {
          if (spec.defaultName) {
            renames[spec.key] = prefix ? `${prefix}-${spec.defaultName}` : spec.defaultName;
          }
        }
        this.fileRenames.set(renames);
      });
    });

    // Apply default file selections once the files are available. This only
    // FILLS empty keys — it never overwrites a selection or custom path that
    // already exists (whether seeded by the JobVolumePicker or chosen by the
    // user), so it is safe to re-run whenever allFiles/runfiles arrive.
    effect(() => {
      const specs = this.fileSpecs();
      const runfiles = this.runfiles();
      const allFiles = this.allFiles();

      untracked(() => {
        const selections = this.fileSelections();
        const customs = this.customPaths();
        for (const spec of specs) {
          // sourceFromJob specs are seeded by the JobVolumePicker itself.
          if (spec.sourceFromJob) continue;
          if (!spec.defaultSelectionRegex) continue;
          if (selections[spec.key] || customs[spec.key]) continue;
          const files = spec.source === 'runfiles' ? runfiles : allFiles;
          const re = new RegExp(spec.defaultSelectionRegex, 'i');
          const defaultFile = files.find(f => re.test(f.name));
          if (defaultFile) {
            this.fileSelections.update((s) => ({ ...s, [spec.key]: defaultFile }));
          }
        }
      });
    });

    // When xwi_prefix changes, update default renames for specs that have a defaultName
    effect(() => {
      const prefix = this.xwiPrefix();
      const specs = untracked(() => this.fileSpecs());
      this.fileRenames.update(current => {
        const next = { ...current };
        for (const spec of specs) {
          if (spec.defaultName) {
            next[spec.key] = prefix ? `${prefix}-${spec.defaultName}` : spec.defaultName;
          }
        }
        return next;
      });
    });

    // Reset extras when the template job changes
    effect(() => {
      this.defaultBasePath();
      this.extraKeys.set([]);
    });

    effect(() => {
      this.resolvedSelections.set(this._resolved());
    });

    effect(() => {
      this.resolvedRenames.set(this.fileRenames());
    });
  }

  protected addFile(): void {
    this.extraKeys.update(keys => [...keys, `extra_${this._extraKeyCounter++}`]);
  }

  protected removeExtra(key: string): void {
    this.fileSelections.update(s => { const n = { ...s }; delete n[key]; return n; });
    this.customPaths.update(s => { const n = { ...s }; delete n[key]; return n; });
    this.fileRenames.update(r => { const n = { ...r }; delete n[key]; return n; });
    this.extraKeys.update(keys => keys.filter(k => k !== key));
  }

  protected setRename(key: string, value: string): void {
    this.fileRenames.update(r => ({ ...r, [key]: value }));
  }

  protected setJobVolumeSelection(key: string, value: FileMeta | null): void {
    this.customPaths.update((s) => { const n = { ...s }; delete n[key]; return n; });
    this.fileSelections.update((s) => ({ ...s, [key]: value }));
  }

  protected setSelection(key: string, value: FileMeta): void {
    this.customPaths.update((s) => { const n = { ...s }; delete n[key]; return n; });
    this.fileSelections.update((s) => ({ ...s, [key]: value }));
  }

  protected setCustomPath(key: string, value: string): void {
    if (value) {
      this.fileSelections.update((s) => { const n = { ...s }; delete n[key]; return n; });
    }
    this.customPaths.update((s) => ({ ...s, [key]: value }));
  }
}
